import { z } from 'zod';
import { EVENT_DATA, type EventType } from './events';
import {
  consentSchema,
  contextSchema,
  id,
  identitySchema,
  ingestSchema,
  isoTimestamp,
} from './primitives';
import { CURRENT_SCHEMA_VERSION } from './version';

/**
 * Fields the widget sends. Everything here is attacker controlled and must be treated as such:
 * org and brand are deliberately absent, because they are resolved server side from the embed key
 * rather than trusted from the page.
 */
const clientBase = z.object({
  /** Minted by the client so that a retry after a network failure is deduplicated, not double counted. */
  event_id: id('event'),
  occurred_at: isoTimestamp,
  interaction_id: id('interaction'),
  interaction_version: z.number().int().min(1).max(100_000),
  identity: identitySchema,
  consent: consentSchema,
  context: contextSchema,
});

/** Added at ingest. A client that tries to send any of these is rejected, not silently ignored. */
const serverShape = {
  schema_version: z.literal(CURRENT_SCHEMA_VERSION),
  received_at: isoTimestamp,
  org_id: id('org'),
  brand_id: id('brand'),
  ingest: ingestSchema,
};

function clientVariant<T extends EventType>(type: T) {
  return clientBase.extend({ event_type: z.literal(type), data: EVENT_DATA[type] }).strict();
}

function storedVariant<T extends EventType>(type: T) {
  return clientVariant(type).extend(serverShape).strict();
}

/**
 * Invariants that span more than one field, applied identically to client and stored events.
 *
 * These are the rules that keep the privacy promises structural rather than aspirational: no
 * device id without storage consent, and no free text answer sitting inline in an event.
 */
function checkCrossFieldRules(
  event: {
    event_type: EventType;
    identity: { device_id?: string | undefined; identity_ref?: string | undefined };
    consent: { storage_allowed: boolean };
    data: Record<string, unknown>;
  },
  ctx: z.RefinementCtx,
): void {
  if (event.identity.device_id !== undefined && !event.consent.storage_allowed) {
    ctx.addIssue({
      code: 'custom',
      message: 'device_id may only be sent when consent allows storage',
      path: ['identity', 'device_id'],
    });
  }

  if (event.event_type === 'answer') {
    const shapes = ['selected_option_ids', 'value_number', 'value_text_ref'].filter(
      (k) => event.data[k] !== undefined,
    );
    if (shapes.length !== 1) {
      ctx.addIssue({
        code: 'custom',
        message: `an answer carries exactly one of selected_option_ids, value_number or value_text_ref, got ${shapes.length}`,
        path: ['data'],
      });
    }
  }

  if (event.event_type === 'complete') {
    const { score, max_score } = event.data as { score?: number; max_score?: number };
    if (score !== undefined && max_score === undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'score without max_score cannot be interpreted',
        path: ['data', 'max_score'],
      });
    }
    if (score !== undefined && max_score !== undefined && score > max_score) {
      ctx.addIssue({
        code: 'custom',
        message: 'score cannot exceed max_score',
        path: ['data', 'score'],
      });
    }
  }

  if (event.event_type === 'identify') {
    const ref = (event.data as { identity_ref?: string }).identity_ref;
    if (ref !== undefined && event.identity.identity_ref !== ref) {
      ctx.addIssue({
        code: 'custom',
        message: 'an identify event must carry the same identity_ref in its envelope',
        path: ['identity', 'identity_ref'],
      });
    }
  }
}

export const clientEventSchema = z
  .discriminatedUnion('event_type', [
    clientVariant('impression'),
    clientVariant('start'),
    clientVariant('answer'),
    clientVariant('complete'),
    clientVariant('submit'),
    clientVariant('identify'),
  ])
  .superRefine(checkCrossFieldRules);

export const storedEventSchema = z
  .discriminatedUnion('event_type', [
    storedVariant('impression'),
    storedVariant('start'),
    storedVariant('answer'),
    storedVariant('complete'),
    storedVariant('submit'),
    storedVariant('identify'),
  ])
  .superRefine(checkCrossFieldRules);

export type ClientEvent = z.infer<typeof clientEventSchema>;
export type StoredEvent = z.infer<typeof storedEventSchema>;
export type StoredEventOf<T extends EventType> = Extract<StoredEvent, { event_type: T }>;

export interface IngestContext {
  org_id: string;
  brand_id: string;
  received_at: string;
  ingest: z.infer<typeof ingestSchema>;
}

/**
 * Turn a validated client event into a stored event.
 *
 * Server owned fields are applied last and unconditionally, so a client cannot override them even
 * if the strict schemas above were ever loosened.
 */
export function toStoredEvent(event: ClientEvent, context: IngestContext): StoredEvent {
  return storedEventSchema.parse({
    ...event,
    schema_version: CURRENT_SCHEMA_VERSION,
    org_id: context.org_id,
    brand_id: context.brand_id,
    received_at: context.received_at,
    ingest: context.ingest,
  });
}

/**
 * Client clocks are wrong often enough to matter. An `occurred_at` far outside the window around
 * the server's own clock is replaced rather than rejected, because losing the event is worse than
 * losing a little timing precision.
 */
export function clampOccurredAt(
  occurredAt: string,
  receivedAt: string,
  toleranceMs = 24 * 60 * 60 * 1000,
): string {
  const occurred = Date.parse(occurredAt);
  const received = Date.parse(receivedAt);
  if (Number.isNaN(occurred) || Number.isNaN(received)) return receivedAt;
  if (occurred > received + 60_000) return receivedAt;
  if (occurred < received - toleranceMs) return receivedAt;
  return occurredAt;
}
